import satSpecs from "./sat_db/satSpecs";

export const DUPLICATE_CAPTURE_WINDOW_SECONDS = 2;

const getCaptureTimestampMs = (capture) => {
  const [, timestamp] = capture;
  const time = new Date(timestamp).getTime();
  return Number.isFinite(time) ? time : null;
};

const getSatelliteKey = (id) => `${satSpecs[id]?.constellation || "Unknown"}-${id}`;

const compareCaptureTimes = (captureA, captureB) => {
  const timeA = getCaptureTimestampMs(captureA);
  const timeB = getCaptureTimestampMs(captureB);

  if (timeA === null && timeB === null) {
    return 0;
  }

  if (timeA === null) {
    return 1;
  }

  if (timeB === null) {
    return -1;
  }

  return timeA - timeB;
};

export const removeDuplicateCaptures = (
  captures,
  duplicateWindowSeconds = DUPLICATE_CAPTURE_WINDOW_SECONDS
) => {
  if (!Array.isArray(captures)) {
    return [];
  }

  const duplicateWindowMs = duplicateWindowSeconds * 1000;
  const filteredCaptures = [];
  const lastKeptBySatellite = new Map();
  const capturesByTime = [...captures].sort(compareCaptureTimes);

  capturesByTime.forEach((capture) => {
    const [id] = capture;
    const captureTime = getCaptureTimestampMs(capture);
    const satelliteKey = getSatelliteKey(id);
    const previousCapture = lastKeptBySatellite.get(satelliteKey);

    if (
      previousCapture
      && captureTime !== null
      && previousCapture.time !== null
      && captureTime - previousCapture.time <= duplicateWindowMs
    ) {
      filteredCaptures[previousCapture.index] = capture;
      lastKeptBySatellite.set(satelliteKey, {
        index: previousCapture.index,
        time: captureTime,
      });
      return;
    }

    lastKeptBySatellite.set(satelliteKey, {
      index: filteredCaptures.length,
      time: captureTime,
    });
    filteredCaptures.push(capture);
  });

  return filteredCaptures.sort(compareCaptureTimes);
};

export const calculateTimeDiffs = (captures) => {
  const timeDiffs = [];

  for (let i = 1; i < captures.length; i++) {
    const [id1, timestamp1] = captures[i - 1];
    const [id2, timestamp2] = captures[i];
    const sameSatellite = getSatelliteKey(id1) === getSatelliteKey(id2);
    const time1 = new Date(timestamp1);
    const time2 = new Date(timestamp2);
    const diff = (time2 - time1) / 1000;

    if (!sameSatellite || diff > DUPLICATE_CAPTURE_WINDOW_SECONDS) {
      timeDiffs.push(diff);
    }
  }

  return timeDiffs;
};

export const formatRevisitDuration = (seconds) => {
  const numericSeconds = Number(seconds);

  if (!Number.isFinite(numericSeconds) || numericSeconds <= 0) {
    return "0 min";
  }

  const totalMinutes = Math.max(0, Math.round(numericSeconds / 60));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [
    { value: days, singular: "day", plural: "days" },
    { value: hours, singular: "hour", plural: "hours" },
    { value: minutes, singular: "min", plural: "min" },
  ];

  const formattedParts = parts
    .filter((part) => part.value > 0)
    .map((part) => `${part.value} ${part.value === 1 ? part.singular : part.plural}`);

  return formattedParts.length > 0 ? formattedParts.join(" ") : "0 min";
};

export const isSarCapture = (id) => (
  (satSpecs[id]?.spectral_range || "").trim().toUpperCase() === "SAR"
);

export const getHourlyCloudCover = (hourlyData) => {
  if (!hourlyData) {
    return null;
  }

  return hourlyData.cloudcover || hourlyData.cloud_cover || null;
};

export const hasHourlyCloudCover = (hourlyData) => {
  const cloudCover = getHourlyCloudCover(hourlyData);
  return Array.isArray(hourlyData?.time) && Array.isArray(cloudCover);
};

export const getCloudCover = (timestamp, hourlyData) => {
  const cloudCover = getHourlyCloudCover(hourlyData);
  const times = hourlyData?.time;

  if (!Array.isArray(times) || !Array.isArray(cloudCover) || times.length === 0) {
    return null;
  }

  const targetTime = new Date(timestamp.replace("+00:00", "Z")).getTime();
  if (!Number.isFinite(targetTime)) {
    return null;
  }

  const closestTimeIndex = times.reduce((closestIndex, currentTime, index) => {
    const closestDiff = Math.abs(new Date(`${times[closestIndex]}Z`).getTime() - targetTime);
    const currentDiff = Math.abs(new Date(`${currentTime}Z`).getTime() - targetTime);
    return currentDiff < closestDiff ? index : closestIndex;
  }, 0);

  const numericCloudCover = Number(cloudCover[closestTimeIndex]);
  return Number.isFinite(numericCloudCover) ? numericCloudCover : null;
};

export const getNonSarCloudCover = (id, timestamp, hourlyData) => (
  isSarCapture(id) ? null : getCloudCover(timestamp, hourlyData)
);

export const getCloudCoverColor = (cloudCover) => {
  if (cloudCover === null || cloudCover === undefined) {
    return { backgroundColor: "rgba(95, 95, 95, 0.55)", textColor: "white" };
  }

  if (cloudCover < 25) {
    return { backgroundColor: "green", textColor: "white" };
  } else if (cloudCover >= 25 && cloudCover < 50) {
    return { backgroundColor: "yellow", textColor: "black" };
  } else if (cloudCover >= 50 && cloudCover < 75) {
    return { backgroundColor: "orange", textColor: "black" };
  }

  return { backgroundColor: "red", textColor: "white" };
};
